/**
 * lib/push.ts
 * ------------------------------------------------------------------
 * Web push (FCM) enrolment for the browser.
 *
 * A signed-in user opts in; we register a dedicated messaging service
 * worker (public/firebase-messaging-sw.js — kept at its OWN scope so it
 * never collides with the PWA/workbox SW at '/'), obtain an FCM token,
 * and store it on `users/{uid}.fcmTokens[]`. The server's
 * onNotificationCreated trigger then pushes every bell notification to
 * those tokens.
 *
 * Graceful degradation: if the VAPID key is not configured
 * (VITE_FIREBASE_VAPID_KEY), or the browser doesn't support push, or
 * the user denies permission, enrolment is a no-op and the app falls
 * back to in-app + email notifications. Nothing throws to the caller.
 *
 * The messaging SW receives the (public) Firebase config via query
 * params, so no config is hard-coded into the static SW file.
 * ------------------------------------------------------------------
 */
import { arrayUnion, doc } from 'firebase/firestore';
import { db, setDoc } from '@/lib/firebase';

// firebase/messaging is ~1 MB — load it lazily (only when a user actually
// checks availability / enrols) so it never weighs down the main bundle.
type MessagingMod = typeof import('firebase/messaging');
let messagingModPromise: Promise<MessagingMod> | null = null;
function loadMessaging(): Promise<MessagingMod> {
  if (!messagingModPromise) messagingModPromise = import('firebase/messaging');
  return messagingModPromise;
}

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

let foregroundBound = false;

/** True when this browser can do web push AND a VAPID key is configured. */
export async function pushAvailable(): Promise<boolean> {
  if (!VAPID_KEY) return false;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('Notification' in window)) {
    return false;
  }
  try {
    const { isSupported } = await loadMessaging();
    return await isSupported();
  } catch {
    return false;
  }
}

/** Current Notification permission, or 'unsupported'. */
export function pushPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/** Build the messaging-SW URL carrying the public Firebase config. */
function messagingSwUrl(): string {
  const params = new URLSearchParams({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
    appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
  });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}

async function registerMessagingSw(): Promise<ServiceWorkerRegistration | null> {
  try {
    // Reuse an existing registration at our scope if present.
    const existing = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE);
    if (existing) return existing;
    return await navigator.serviceWorker.register(messagingSwUrl(), { scope: FCM_SW_SCOPE });
  } catch (e) {
    console.warn('[push] messaging SW registration failed:', e);
    return null;
  }
}

/** Bind a foreground handler so a message received while the tab is
 *  focused still raises a native notification. */
async function bindForeground() {
  if (foregroundBound) return;
  try {
    const { getMessaging, onMessage } = await loadMessaging();
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      const title = payload.notification?.title || payload.data?.title;
      const body = payload.notification?.body || payload.data?.body || '';
      if (!title) return;
      if (Notification.permission === 'granted') {
        try {
          new Notification(title, { body, icon: '/images/lkc_logo.png', tag: payload.data?.type || 'lkc-notice' });
        } catch {
          /* some browsers require the SW to show it; ignore */
        }
      }
    });
    foregroundBound = true;
  } catch (e) {
    console.warn('[push] foreground bind failed:', e);
  }
}

/**
 * Ensure this user is enrolled for push. Safe to call on every load.
 *
 * @param uid    the signed-in user's auth uid
 * @param prompt when true, request Notification permission if not yet
 *               decided; when false, only (re)enroll if already granted
 *               (silent token refresh).
 * @returns 'enrolled' | 'denied' | 'unsupported' | 'dismissed' | 'error'
 */
export async function enrollPush(
  uid: string,
  prompt: boolean,
): Promise<'enrolled' | 'denied' | 'unsupported' | 'dismissed' | 'error'> {
  if (!(await pushAvailable())) return 'unsupported';

  let perm = Notification.permission;
  if (perm === 'denied') return 'denied';
  if (perm === 'default') {
    if (!prompt) return 'dismissed';
    try {
      perm = await Notification.requestPermission();
    } catch {
      return 'error';
    }
    if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'dismissed';
  }

  const swReg = await registerMessagingSw();
  if (!swReg) return 'error';

  try {
    const { getMessaging, getToken } = await loadMessaging();
    const messaging = getMessaging();
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return 'error';

    await setDoc(
      doc(db, 'users', uid),
      { fcmTokens: arrayUnion(token) },
      { merge: true },
    );
    await bindForeground();
    return 'enrolled';
  } catch (e) {
    console.warn('[push] getToken/store failed:', e);
    return 'error';
  }
}
